/* BLUE CALENDAR — MARVI OCEAN
   Shared route + stop data. Used by both bc-globe.js (3D) and bc-map2d.js (2D).
   Routes are illustrative corridors grounded in published research — NOT live GPS tracks.
   All waypoints cross-checked to sit in open water only.

   GEOGRAPHIC AUDIT (2026-07-14) — full 41-species pass via 5 independent research agents,
   each verifying against NOAA Fisheries, IUCN Red List, IWC, WWF, national wildlife agencies,
   and published satellite-tagging studies. Real, sourced corrections applied throughout this
   file. Three species were split because they conflated genuinely separate, non-connected
   populations/phenomena into one false "route":
     - southernrightwhale -> southernrightwhale-argentina + southernrightwhale-southafrica
       (two distinct non-migrating populations on different continents)
     - orca-north-pacific-resident -> orca-southern-resident + orca-northern-resident
       (genetically/culturally distinct, non-interbreeding populations per Canada DFO)
     - whaleshark-philippines -> whaleshark-donsol + whaleshark-oslob
       (Donsol is a real wild seasonal aggregation; Oslob is a hand-fed, largely non-migratory
       provisioning site — no tagging study supports the 14-waypoint loop that previously
       connected them across the entire Philippine archipelago)
   Two isResident flags corrected in bc-data (see those files): manta-reef-kona stays true
   (confirmed genuine year-round residents, Manta Pacific Research Foundation); 
   manta-giant-ningaloo corrected to false (this waypoint is the transient Exmouth coral-spawn
   aggregation, distinct from Coral Bay's true residents, per Project Manta/Murdoch University).
   Every change below has a real source cited in sourceNotes.

   MARVI ATLAS CROSS-CHECK (2026-07-14, later same day) — cross-referenced against
   src/data/migrationStops.ts in the Marvi Atlas project, itself pulled from an earlier
   (2026-07-07) backup of this same Blue Calendar data. That snapshot predates the audit
   above, so it was NOT used to overwrite any already-corrected coordinate (it still has the
   old Sri Lanka/Chagos mislabel, the old unsplit species, and other coordinates already fixed
   here). Two things from it WERE used:
     1. Added the missing "commondolphin-northeast-atlantic" route below — bc-data-part4.js
        has a full commondolphin-northeast-atlantic species record and bc-globe.js's COLORS
        map already had an entry for this key, but no MIGRATORS route existed for it, so this
        species could never appear on the map at all (same bug class as the manta isResident
        issue). Route waypoints sourced from Marvi Atlas's real UK/Ireland common dolphin data.
     2. Added real per-waypoint "content" text (a sourced sentence or two per stop) to waypoints
        whose corrected coordinates and purpose still line up with Marvi Atlas's version —
        skipped entirely wherever the Marvi coordinate was one of the ones fixed in the audit
        above, so no bug was reintroduced. bc-globe.js's stop-click panel now shows this text
        when present, instead of always showing generic boilerplate.
*/
window.BC_MIGRATORS = {

  // ===== HUMPBACK WHALES — 6 DISTINCT POPULATIONS/ROUTES =====
  "humpback-australia-east": {
    name: "Humpback Whale - East Australia",
    waypoints: [
      { lat: -64, lng: 130,    label: "Antarctic feeding grounds",      purpose: "feed",
        content: "Southern Hemisphere humpbacks spend the austral summer feeding almost exclusively on Antarctic krill in these productive waters south of Australia, building the blubber reserves that fuel the entire round-trip migration — they feed little to nothing again until they return the following summer." },
      { lat: -48, lng: 147,    label: "Tasman Sea transit",              purpose: "transit",
        content: "A deep-water corridor between the Antarctic feeding grounds and the Queensland breeding grounds. Whales pass through steadily rather than lingering, covering this leg during the most direct, sustained-swimming stretch of the migration." },
      { lat: -33, lng: 154.5,  label: "Offshore NSW coast",              purpose: "transit",
        content: "Migrating whales hug this stretch of the NSW continental shelf on both legs of the journey. Its proximity to Sydney makes it one of the most closely observed sections of the East Australian humpback corridor, with land-based surveys running every winter and spring." },
      { lat: -28.6, lng: 154.3,label: "Byron Bay headland",              purpose: "transit" },
      { lat: -25.3, lng: 153.8,label: "Hervey Bay",                      purpose: "rest",
        content: "Hervey Bay, shielded by K'gari (Fraser Island), is a documented stopover early in the southbound migration — used less for simple rest than for active social behaviour: mothers spend the bulk of their time here nursing and bonding with calves in sheltered, shallow water before the cohort continues toward Antarctica, while other whales form and dissolve new social pods." },
      { lat: -22.0, lng: 152.2,label: "Offshore Central Queensland",     purpose: "transit" },
      { lat: -16.9, lng: 146.9,label: "Great Barrier Reef",              purpose: "breed",
        content: "Warm, reef-sheltered waters off Cairns and the Whitsundays serve as the winter breeding and calving grounds for the East Australian humpback population, the world's largest recovering humpback stock — numbering only a few hundred after commercial whaling but now in the tens of thousands." }
    ],
    cycleMonths: [4,5,6,7,8,9,10],
    stages: [
      "Feeding in Antarctic (Jan-Mar)",
      "Northbound migration (Apr-May)",
      "Arriving east coast (May-Jun)",
      "Byron Bay transit (Sep)",
      "Resting/nursery stopover, Hervey Bay (Jul-Sep)",
      "Breeding GBR (Mar-Aug)",
      "Southbound return (Sep-Nov)"
    ],
    sourceNotes: "NOAA Fisheries, DCCEEW, WWF Australia, UniSC Oceania Project (2024). Hervey Bay corrected from 'breed' to 'rest' — it's a mother-calf resting/nursery stopover on the southern migration, not a calving ground (Frontiers 2024, fmars.2024.1426248)."
  },

  "humpback-australia-west": {
    name: "Humpback Whale - West Australia (Ningaloo)",
    waypoints: [
      { lat: -58, lng: 95,     label: "Kerguelen Plateau feeding grounds", purpose: "feed",
        content: "The Western Australian ('Group IV') population feeds on Antarctic krill in this Indian Ocean sector of the Southern Ocean from roughly November to May — among the longest feeding seasons of any humpback population, fuelling one of the longest migrations of any mammal on Earth." },
      { lat: -50, lng: 100,    label: "Southern Ocean transit",          purpose: "transit",
        content: "Open Southern Ocean corridor crossed on the migration between the Antarctic feeding grounds and the Western Australian coast." },
      { lat: -35, lng: 108,    label: "Indian Ocean approach",           purpose: "transit" },
      { lat: -28, lng: 111,    label: "Offshore Mid-West WA coast",      purpose: "transit" },
      { lat: -24, lng: 112.3,  label: "Shark Bay region",                purpose: "rest" },
      { lat: -21.8, lng: 113.6,label: "Ningaloo Reef & Exmouth Gulf",   purpose: "breed" }
    ],
    cycleMonths: [3,4,5,6,7,8],
    stages: [
      "Feeding, Kerguelen Plateau (Dec-Mar)",
      "Northbound migration begins (Feb)",
      "Arriving west coast (Mar-Apr)",
      "Peak breeding Ningaloo (Apr-Jun)",
      "Southbound return begins (Jul-Aug)",
      "Return to Kerguelen Plateau (Aug-Sep)"
    ],
    sourceNotes: "NOAA Fisheries, DCCEEW, WWF Australia. Feeding ground corrected from a copy-pasted east-coast Antarctic point to the satellite-confirmed Kerguelen Plateau sector (~50-60°S, 80-110°E) this population actually uses (Riekkola et al., PMC6764985)."
  },

  "humpback-north-pacific": {
    name: "Humpback Whale - North Pacific (Alaska to Hawaii)",
    waypoints: [
      { lat: 58.0, lng: -137.6,label: "SE Alaska / Glacier Bay",        purpose: "feed",
        content: "Cold, nutrient-rich waters around Glacier Bay and Frederick Sound support summer blooms of krill, herring, and capelin. More than 500 humpbacks return to Southeast Alaska each summer to feed here, some using cooperative bubble-net feeding to concentrate prey." },
      { lat: 50,   lng: -145,   label: "Gulf of Alaska transit",         purpose: "transit" },
      { lat: 40,   lng: -150,   label: "North Pacific transit",          purpose: "transit",
        content: "Open North Pacific crossing on the roughly 5,000km migratory route between Alaskan feeding grounds and Hawaiian breeding waters — one of the longest documented migrations of any mammal." },
      { lat: 25,   lng: -157,   label: "Approach to Hawaii",             purpose: "transit",
        content: "The final open-ocean approach as whales close in on the main Hawaiian Islands, where roughly half of the entire North Pacific humpback population converges each winter." },
      { lat: 20.78,lng: -156.72,label: "Au'au Channel, Maui",           purpose: "breed",
        content: "The channel between Maui, Molokaʻi, and Lānaʻi holds the highest concentration of North Pacific humpbacks anywhere in Hawaiʻi — up to several thousand at peak season — making it the population's primary breeding and calving ground. NOAA estimates over 11,000 humpbacks migrate to Hawaiian waters annually." },
      { lat: 19.62,lng: -156.28,label: "Kona coast, Big Island",         purpose: "breed" }
    ],
    cycleMonths: [10,11,0,1,2,3,4],
    stages: [
      "Feeding, SE Alaska (Jun-Sep)",
      "Southbound migration begins (Oct)",
      "Traveling open North Pacific (Oct-Dec)",
      "Arriving Hawaiian waters (Dec)",
      "Peak breeding Hawaii (Jan-Mar)",
      "Northbound return (Apr-May)",
      "Arriving Alaska feeding grounds (Jun)"
    ],
    sourceNotes: "NOAA Fisheries, IWC, Ocean Wise. Verified accurate — no changes."
  },

  "humpback-north-pacific-mexico": {
    name: "Humpback Whale - North Pacific (Alaska to Baja Mexico)",
    waypoints: [
      { lat: 58.0, lng: -137.6,label: "SE Alaska / Glacier Bay",        purpose: "feed" },
      { lat: 50,   lng: -145,   label: "Gulf of Alaska transit",         purpose: "transit" },
      { lat: 40,   lng: -135,   label: "Pacific Northwest transit",      purpose: "transit" },
      { lat: 30,   lng: -120,   label: "California coast transit",       purpose: "transit" },
      { lat: 24.5, lng: -112.5,label: "Magdalena Bay, Baja Pacific side",purpose: "breed" },
      { lat: 21.5, lng: -110.0,label: "Open Pacific south of Cabo",      purpose: "transit" },
      { lat: 24.5, lng: -108.8,label: "Gulf of California, open water",  purpose: "breed" }
    ],
    cycleMonths: [10,11,0,1,2,3,4],
    stages: [
      "Feeding, SE Alaska (Jun-Sep)",
      "Southbound migration begins (Oct)",
      "Traveling down North America coast (Oct-Dec)",
      "Arriving Baja breeding grounds (Dec)",
      "Peak breeding Baja (Jan-Mar)",
      "Northbound return (Apr-May)",
      "Returning to Alaska (Jun)"
    ],
    sourceNotes: "NOAA Fisheries, Ocean Wise, IWC. Verified the south-of-Cabo to Gulf-of-California segment stays in open water (~50km clearance from the Baja peninsula) — no land crossing."
  },

  "humpback-north-atlantic": {
    name: "Humpback Whale - North Atlantic",
    waypoints: [
      { lat: 70,   lng: -22,    label: "Iceland feeding grounds",       purpose: "feed",
        content: "North Atlantic humpbacks spend the summer feeding on krill and sandeel in Icelandic and Greenlandic waters, the population's main high-latitude feeding range, before making the long crossing south to Caribbean breeding grounds." },
      { lat: 65,   lng: -30,    label: "Denmark Strait / Irminger Sea feeding", purpose: "feed" },
      { lat: 50,   lng: -35,    label: "North Atlantic transit",        purpose: "transit",
        content: "Open-ocean stretch of the North Atlantic migratory corridor connecting the Iceland/Greenland feeding grounds to the Caribbean breeding grounds — thousands of kilometres of the crossing with no significant stopover." },
      { lat: 32,   lng: -55,    label: "Sargasso Sea transit",          purpose: "transit",
        content: "Whales cross this stretch of the Sargasso Sea, a current-bound gyre better known as the spawning ground of Atlantic eels, without stopping — it lies directly on the great-circle route between the northern feeding grounds and the Dominican Republic." },
      { lat: 21.0, lng: -69.8,  label: "Silver Bank, Dominican Rep",    purpose: "breed",
        content: "A shallow offshore bank protected since 1986 as the Sanctuary for the Marine Mammals of the Dominican Republic. It hosts the largest known breeding and calving aggregation of North Atlantic humpbacks — several thousand whales gather here each winter to court, mate, and give birth." },
      { lat: 19.1, lng: -69.1,  label: "Samaná Bay approach, Dominican Rep", purpose: "rest",
        content: "A sheltered coastal bay that functions as a nursery: warm, calm, shallow water lets newborn calves — already around 4m long — nurse and build strength before the return migration north, with mothers and calves using the bay from January to April." }
    ],
    cycleMonths: [10,11,0,1,2,3,4],
    stages: [
      "Feeding Iceland/Denmark Strait (May-Sep)",
      "Southbound migration (Oct-Nov)",
      "Open Atlantic crossing (Nov-Dec)",
      "Arriving Caribbean breeding (Dec)",
      "Peak breeding Silver Bank (Jan-Mar)",
      "Northbound return (Apr-May)",
      "Returning to feeding grounds (Jun)"
    ],
    sourceNotes: "NOAA Fisheries, IWC, Dominican Republic Ministry. Relabeled the second feeding point — (65,-30) is the Denmark Strait/Irminger Sea (SW of Iceland toward Greenland), not the Norwegian Sea, which sits ~30° further east (Sargasso Sea Commission IMMA report)."
  },

  "humpback-south-pacific": {
    name: "Humpback Whale - South Pacific (Antarctica to Polynesia)",
    waypoints: [
      { lat: -65, lng: 172,     label: "Antarctic feeding grounds (Area V/VI)", purpose: "feed" },
      { lat: -52, lng: 176,     label: "Southern Ocean transit",        purpose: "transit" },
      { lat: -38, lng: -179,    label: "South Pacific transit",         purpose: "transit" },
      { lat: -23, lng: -177,    label: "South of Fiji transit",         purpose: "transit" },
      { lat: -18.9, lng: -174.3,label: "Vava'u, Tonga breeding",        purpose: "breed" },
      { lat: -17, lng: -149,    label: "French Polynesia breeding",     purpose: "breed" }
    ],
    cycleMonths: [7,8,9,10],
    stages: [
      "Feeding, Antarctic Area V/VI (Nov-Mar)",
      "Northbound migration begins (Apr)",
      "Traveling tropical waters (May-Jun)",
      "Arriving Polynesia (Jul)",
      "Peak breeding Tonga (Aug-Sep)",
      "Breeding Polynesia (Aug-Oct)",
      "Southbound return (Nov-Dec)"
    ],
    sourceNotes: "IWC, WWF, Tonga Department of Fisheries. Rerouted — the feeding ground was previously placed at 60°E (south of Africa/India), a ~9,000km detour with no support. Genotype-matching studies (Polar Biology 2017, doi 10.1007/s00300-017-2226-9) link Oceania breeding stocks to Antarctic Areas V/VI, directly south of the breeding grounds near 165-180°E."
  },

  // ===== BLUE WHALE =====
  "bluewhale-northeast-pacific": {
    waypoints: [
      { lat: 51,  lng: -151,    label: "Gulf of Alaska / Aleutians",    purpose: "feed",
        content: "The northern edge of this population's range, where cold sub-Arctic water supports the dense krill swarms blue whales need — they must find patches denser than about 100 krill per cubic metre to make lunge-feeding worthwhile." },
      { lat: 40,  lng: -140,    label: "Pacific transit south",          purpose: "transit",
        content: "Open North Pacific crossing between the Gulf of Alaska feeding range and the California Current, generally travelled without stopping to feed." },
      { lat: 32,  lng: -120.5,  label: "California Current, Channel Is.",purpose: "feed",
        content: "The Santa Barbara Channel and Channel Islands sit at the intersection of upwelling zones, submarine canyons, and the California Current — conditions that concentrate krill so reliably this is considered one of the most important blue whale feeding areas on Earth each summer and autumn." },
      { lat: 24,  lng: -117,    label: "Offshore Baja Pacific side",     purpose: "transit" },
      { lat: 15,  lng: -100,    label: "Offshore Central America transit", purpose: "transit" },
      { lat: 9,   lng: -90,     label: "Costa Rica Dome",                purpose: "breed" }
    ],
    cycleMonths: [7,8,9,10,11,0,1,2],
    stages: ["Feeding, Gulf of Alaska","Southbound, open Pacific","Feeding, California Current","Breeding area, Costa Rica Dome"],
    sourceNotes: "NOAA/OSU satellite tagging (Bruce Mate et al.) confirms the Costa Rica Dome (~9°N,90°W) as the real calving terminus. The old single waypoint at (18,-105) blended this with Revillagigedo Islands (~18.7°N,111°W), a different site ~1,600km away with weak evidence of a blue whale breeding role."
  },
  "bluewhale-southern-hemisphere": {
    waypoints: [
      { lat: -65, lng: 60,      label: "Antarctic feeding grounds",      purpose: "feed",
        content: "Summer krill feeding grounds in the Southern Ocean south of the Indian Ocean — part of the same circumpolar Antarctic krill fishery that historically drew the whaling fleets which brought this population close to extinction." },
      { lat: -48, lng: 65,      label: "Southern Ocean transit",          purpose: "transit",
        content: "Open-water leg of the Southern Ocean migratory corridor between Antarctic feeding grounds and the northbound route toward the Indian Ocean." },
      { lat: -25, lng: 72,      label: "Indian Ocean, northbound",        purpose: "transit",
        content: "Migratory corridor through the open Indian Ocean, part of the long-distance route between Southern Ocean feeding grounds and equatorial waters." },
      { lat: 5.9, lng: 80.5,    label: "Mirissa, south Sri Lanka",       purpose: "feed" },
      { lat: 8.5, lng: 81.2,    label: "Trincomalee, northeast Sri Lanka", purpose: "feed" }
    ],
    cycleMonths: [11,0,1,2,3,4,5,6,7],
    stages: ["Feeding, Antarctic","Northbound, Southern Ocean","Crossing Indian Ocean","Aggregating off Mirissa, south coast (Dec-Apr)","Aggregating off Trincomalee, NE coast (Mar-Aug)"],
    sourceNotes: "de Vos et al. 2011; Randage et al. 2015 (PMC8062560). Corrected — the final waypoint used to sit at (-8,73), matching Chagos Archipelago, but was labeled 'Sri Lanka'; the site's own encyclopedia card is written entirely around this real, documented Sri Lanka pygmy blue whale population (Mirissa Dec-Apr, Trincomalee Mar-Aug) with specific tour-operator ethics guidance, so the fix was to correct the coordinates to real Sri Lanka, not to relabel away from it."
  },

  // ===== FIN WHALE =====
  "finwhale-north-atlantic": {
    waypoints: [
      { lat: 68,  lng: -10,     label: "Iceland / Norwegian Sea",        purpose: "feed",
        content: "Icelandic waters hold one of the largest fin whale feeding populations in the North Atlantic, drawn by dense summer krill and small schooling fish. Iceland is also one of the last countries still permitting commercial fin whaling, making this population a focus of ongoing conservation debate." },
      { lat: 66,  lng: -5,      label: "East of Iceland, open water",    purpose: "transit" },
      { lat: 55,  lng: -15,     label: "South of Iceland, open Atlantic",purpose: "transit" },
      { lat: 55,  lng: -30,     label: "North Atlantic transit",          purpose: "transit" },
      { lat: 38.5,lng: -28,     label: "Azores, mid-Atlantic",           purpose: "feed",
        content: "Seamounts around the Azores create localised upwelling that concentrates prey, making this mid-Atlantic archipelago a documented secondary feeding stop for fin whales migrating between Iceland and lower latitudes." },
      { lat: 34,  lng: -22,     label: "Open Atlantic, south of Azores", purpose: "transit" },
      { lat: 32,  lng: -17,     label: "Madeira / Canaries area",        purpose: "transit",
        content: "Migratory waters off Madeira and the Canary Islands, a stretch of the eastern Atlantic corridor connecting northern feeding grounds to subtropical wintering waters." },
      { lat: 16,  lng: -24,     label: "Cape Verde / tropical Atlantic", purpose: "breed",
        content: "Warmer waters off Cape Verde are among the less-studied areas associated with fin whale winter breeding activity — fin whale mating and calving grounds are still poorly documented compared to feeding areas, since breeding largely happens in open water rather than at fixed coastal sites." }
    ],
    cycleMonths: [9,10,11,0,1,2,3],
    stages: ["Feeding, Iceland / Norwegian Sea","Southbound, open Atlantic","Feeding stopover, Azores","Passing Madeira & Canaries","Breeding grounds, Cape Verde area"],
    sourceNotes: "Silva et al. 2013, PLOS ONE. Azores point corrected from (43,-35) to (38.5,-28) — real archipelago spans ~36.5-40°N/24.5-31.5°W. Cape Verde corrected from (20,-23) to (16,-24) — real islands span ~14-18°N/22-26°W."
  },
  "finwhale-north-pacific": {
    waypoints: [
      { lat: 56,  lng: -155,    label: "Gulf of Alaska feeding",         purpose: "feed",
        content: "North Pacific fin whales feed here on krill and small fish such as capelin and herring through the summer, in the same productive Gulf of Alaska waters used by several other baleen whale species." },
      { lat: 42,  lng: -145,    label: "North Pacific transit",          purpose: "transit",
        content: "Open North Pacific waters crossed on the route between Gulf of Alaska feeding grounds and lower-latitude waters off Baja California." },
      { lat: 28,  lng: -115,    label: "Baja California, winter range",  purpose: "rest",
        content: "Warmer waters off Baja California used as wintering habitat by the Northeast Pacific fin whale population, though — as with the Atlantic population — precise breeding locations for this species remain less well mapped than its feeding grounds." }
    ],
    cycleMonths: [9,10,11,0,1,2],
    stages: ["Feeding, Gulf of Alaska","Southbound, North Pacific","Winter aggregation, Baja California"],
    sourceNotes: "NOAA Fisheries notes fin whale migration is complex and specific breeding grounds aren't well documented for this species (unlike gray whales) — relabeled the Baja stop from 'breed' to 'rest' since no source confirms it as a calving ground, just a winter range."
  },

  // ===== GRAY WHALE =====
  "graywhale-eastern-pacific": {
    waypoints: [
      { lat: 65,  lng: -169,    label: "Chukchi / Bering Sea feeding",  purpose: "feed",
        content: "Unlike most baleen whales, gray whales are bottom-feeders: they roll onto their side and suction amphipods and other invertebrates out of the seafloor sediment. This shallow Arctic shelf is the population's main summer feeding ground, built up over the short ice-free season." },
      { lat: 55,  lng: -163,    label: "Southbound, Alaska coast",       purpose: "transit",
        content: "Whales hug the Alaskan coastline on the southbound leg of what is one of the longest annual migrations of any mammal — roughly 16,000-20,000km round trip between the Arctic and Baja California." },
      { lat: 40,  lng: -130,    label: "Oregon / California coast",      purpose: "transit",
        content: "Gray whales migrate closer to shore than almost any other large whale, making this stretch of the Oregon/California coast one of the most reliable land-based whale-watching corridors in the world during the winter southbound and spring northbound passages." },
      { lat: 27.75,lng: -114.25,label: "Laguna Ojo de Liebre (Scammon's)",purpose: "breed",
        content: "Named for whaler Charles Melville Scammon, who discovered the lagoon in 1857 and nearly wiped out the population hunting here. It's now a protected calving and mating lagoon within the same El Vizcaíno reserve as San Ignacio, and a symbol of the species' recovery from near-extinction." },
      { lat: 26.87,lng: -113.18,label: "San Ignacio Lagoon, Baja",      purpose: "breed",
        content: "A shallow, protected lagoon within the El Vizcaíno Biosphere Reserve (UNESCO World Heritage). It's one of the last major gray whale calving lagoons undeveloped by tourism infrastructure, and is famous for \"friendly whales\" — mothers that approach small boats and bring their calves alongside." },
      { lat: 24.58,lng: -112.0, label: "Bahía Magdalena / Puerto San Carlos", purpose: "breed" }
    ],
    cycleMonths: [10,11,0,1,2,3,4,5],
    stages: ["Feeding, Chukchi / Bering Sea","Southbound along Alaska coast","Passing Oregon / California coast","Breeding, Laguna Ojo de Liebre (Scammon's)","Breeding, San Ignacio Lagoon","Breeding, Bahía Magdalena"]
  },

  // ===== MINKE WHALE =====
  "minke-north-atlantic": {
    waypoints: [
      { lat: 72,  lng: 20,      label: "Svalbard / Barents Sea",        purpose: "feed",
        content: "Minke whales — the smallest of the baleen whales in this region — feed on krill and small schooling fish in these high-Arctic waters around Svalbard through the short polar summer, taking advantage of the intense but brief productivity of the ice-edge bloom." },
      { lat: 62,  lng: 2,       label: "Norwegian Sea transit",          purpose: "transit",
        content: "Coastal Norwegian waters used as a migratory corridor between Barents Sea/Svalbard feeding grounds and lower-latitude range further south. Norway is also one of the few nations still conducting commercial minke whaling under IWC objection, concentrated along this coast." },
      { lat: 61,  lng: -1,      label: "North Sea, east of Shetland",    purpose: "transit" },
      { lat: 58,  lng: -10,     label: "West of Scotland, open Atlantic",purpose: "transit" },
      { lat: 54,  lng: -13,     label: "West of Ireland, open Atlantic", purpose: "transit" },
      { lat: 50,  lng: -11,     label: "Celtic Sea / SW Ireland",        purpose: "feed",
        content: "Shelf waters off southwest Ireland are a well-documented minke whale feeding ground, where whales target sandeel and other small shoaling fish concentrated along the continental shelf edge." },
      { lat: 28,  lng: -16,     label: "Canary Islands, southern winter range", purpose: "transit" }
    ],
    cycleMonths: [3,4,5,6,7,8,9],
    stages: ["Feeding, Svalbard & Barents Sea","Southbound, Norwegian coast","Feeding, Celtic Sea","Canary Islands winter range"],
    sourceNotes: "Movement Ecology 2015. Corrected final waypoint from (35,-15), which is offshore Portugal, to the real Canary Islands (~28°N) — satellite tracking shows minkes moving south along the Mid-Atlantic Ridge to at least this latitude."
  },
  "minke-antarctic": {
    waypoints: [
      { lat: -68, lng: -55,     label: "Antarctic Peninsula feeding",   purpose: "feed",
        content: "Antarctic minke whales feed heavily on Antarctic krill along the ice edge of the Peninsula, often penetrating further into pack ice than most other baleen whales — a habit that has made them the main target of Japan's Antarctic research/commercial minke hunt." },
      { lat: -52, lng: -40,     label: "Southern Ocean transit",         purpose: "transit",
        content: "Open Southern Ocean corridor used on the migration between Antarctic Peninsula feeding grounds and lower-latitude South Atlantic waters." },
      { lat: -7,  lng: -34,     label: "Northeastern Brazil (Paraíba coast) breeding ground", purpose: "breed" }
    ],
    cycleMonths: [9,10,11,0,1,2,3,4],
    stages: ["Feeding, Antarctic Peninsula","Northbound, Southern Ocean","Only documented low-latitude breeding ground, NE Brazil"],
    sourceNotes: "IMMA factsheet, 'Northeastern Brazil Antarctic Minke Whale Breeding Habitat'. The only documented breeding ground (from historical whaling records at Costinha Station) is off NE Brazil at ~6-8°S,34°W — corrected from (-25,-38), which is off São Paulo/Rio, ~2,000km away and not a documented breeding area."
  },

  // ===== SEI WHALE =====
  "seiwhale-north-atlantic": {
    waypoints: [
      { lat: 57,  lng: -52,     label: "Labrador Sea feeding grounds",   purpose: "feed" },
      { lat: 47,  lng: -40,     label: "Open North Atlantic transit",    purpose: "transit" },
      { lat: 38.5,lng: -28,     label: "Azores, spring staging",         purpose: "transit" },
      { lat: 20.8,lng: -17.1,   label: "Off Mauritania/Western Sahara (Cap Blanc), winter grounds", purpose: "breed" }
    ],
    cycleMonths: [9,10,11,0,1,2,3],
    stages: ["Feeding, Labrador Sea","Southbound, open North Atlantic","Spring staging, Azores","Winter grounds, off Mauritania"],
    sourceNotes: "Prieto et al. 2014 satellite telemetry; J. Cetacean Res. Manage. ('Upwellings, canyons and whales... off Mauritania'). Fully rerouted — the documented tracked corridor is Labrador Sea to the Mauritania/Western Sahara upwelling, not an Iceland-anchored route through mid-ocean waters ~1,500km from any coastal wintering ground."
  },

  // ===== SPERM WHALE =====
  "spermwhale-north-pacific": {
    waypoints: [
      { lat: 45,  lng: -155,    label: "North Pacific deep-water feeding (mature males)",purpose: "feed",
        content: "Mature males range solo into these higher-latitude waters to dive for squid, including large deep-sea species, at depths that can exceed 1,000m — far beyond the tropical range used by female and juvenile pods." },
      { lat: 25,  lng: -140,    label: "Subtropical transit",            purpose: "transit",
        content: "Open subtropical Pacific crossed by males moving between high-latitude feeding grounds and the warmer waters where they periodically rejoin female groups to breed." },
      { lat: 10,  lng: -120,    label: "Tropical Pacific, year-round resident range (females/family units)",purpose: "rest",
        content: "Warm tropical Pacific waters where stable matriarchal pods of females and young remain year-round. Large males travel here from higher latitudes to compete for access to these groups during the breeding season." }
    ],
    cycleMonths: [0,1,2,3,4,5,6,7,8,9,10,11],
    stages: ["Deep-water feeding, North Pacific (mature males only)","Southbound toward tropics","Year-round range, females & family units"],
    sourceNotes: "NOAA Fisheries. Sperm whales aren't strongly seasonal migrators — only maturing/adult males make excursions to high-latitude feeding grounds; females/immatures stay resident in tropical waters year-round. Relabeled the tropical waypoint from a discrete 'breed' stop to a resident range, removing false seasonal-aggregation framing."
  },

  // ===== SOUTHERN RIGHT WHALE — split into two non-connected populations =====
  "southernrightwhale-argentina": {
    name: "Southern Right Whale - Argentina (Península Valdés)",
    waypoints: [
      { lat: -42.0,lng: -63.3,  label: "Península Valdés, Argentina — calving & nursery ground", purpose: "breed",
        content: "The calm, sheltered Golfo Nuevo and Golfo San José at Península Valdés (a UNESCO World Heritage site) have been used as southern right whale nursery grounds for over a thousand years. Shallow, wave-protected water lets calves — born without the thick blubber of adults — rest, nurse, and surface easily without expending excess energy." },
      { lat: -46,  lng: -58,    label: "Patagonian shelf, southbound transit", purpose: "transit",
        content: "Coastal Argentine shelf waters crossed on the return leg south, between the sheltered calving gulfs of Península Valdés and the open-ocean feeding range." },
      { lat: -50,  lng: -45,    label: "Outer Patagonian shelf / slope front feeding", purpose: "feed",
        content: "Sub-Antarctic South Atlantic waters where southern right whales feed on copepods and krill through the austral summer, building the reserves that let adults fast for months while calving in Argentine bays." },
      { lat: -54.5,lng: -38,    label: "Scotia Sea / South Georgia feeding grounds", purpose: "feed" }
    ],
    cycleMonths: [5,6,7,8,9,10,11],
    stages: ["Feeding, Scotia Sea/South Georgia (Jan-May)","Northbound transit","Calving, Península Valdés (Jun-Dec)"],
    sourceNotes: "peninsulavaldes.com; ResearchGate tracking study. Split from a single 'southernrightwhale' entry that wrongly strung Argentina and South Africa together as one route — these are separate, non-migrating populations on different continents."
  },
  "southernrightwhale-southafrica": {
    name: "Southern Right Whale - South Africa (Cape south coast)",
    waypoints: [
      { lat: -34.45,lng: 19.25, label: "Hermanus / Walker Bay, South Africa", purpose: "breed",
        content: "Walker Bay at Hermanus is considered one of the most reliable southern right whale nurseries in the world. Between June and December, mothers and newborn calves rest within metres of the shoreline in calm, sheltered water — close enough to observe from the cliffside path through town." },
      { lat: -34.5, lng: 20.4,  label: "De Hoop / Koppie Alleen — densest mother-calf concentration on African coast", purpose: "breed" },
      { lat: -36,   lng: 22,    label: "Southern Ocean feeding range south of Africa", purpose: "feed" }
    ],
    cycleMonths: [5,6,7,8,9,10,11],
    stages: ["Calving, Hermanus/Walker Bay (Jun-Dec, peak births Aug)","Calving/nursing, De Hoop","Independent Southern Ocean feeding range"],
    sourceNotes: "Hermanus Online; Whalecoast. Split from the merged 'southernrightwhale' entry — see southernrightwhale-argentina for why."
  },

  // ===== NORTH ATLANTIC RIGHT WHALE =====
  "northatlanticrightwhale": {
    waypoints: [
      { lat: 43,  lng: -67,     label: "Gulf of Maine / Bay of Fundy",  purpose: "feed",
        content: "The traditional feeding ground for this critically endangered population (fewer than 400 remain), historically rich in the copepod Calanus finmarchicus. Warming water has caused a sharp decline in copepod abundance here since 2010, pushing a growing share of whales to shift feeding grounds north into the Gulf of St. Lawrence." },
      { lat: 36,  lng: -74,     label: "Mid-Atlantic transit",           purpose: "transit",
        content: "Coastal Mid-Atlantic corridor between the Gulf of Maine feeding grounds and the Southeast US calving grounds — one of the most heavily trafficked shipping and fishing regions on the whale's route, making it a focus of vessel-speed rules meant to prevent ship strikes." },
      { lat: 30.5, lng: -80.7,  label: "Georgia / NE Florida calving",  purpose: "breed",
        content: "The species' only known calving ground: shallow, warmer coastal waters off Georgia and northeast Florida where pregnant females arrive each winter to give birth, a critical habitat protected under the US Endangered Species Act." }
    ],
    cycleMonths: [10,11,0,1,2,3],
    stages: ["Feeding, Gulf of Maine & Bay of Fundy","Southbound, Mid-Atlantic","Calving grounds, Georgia / NE Florida"],
    sourceNotes: "NOAA Fisheries calving-season page. cycleMonths trimmed from Nov-Jun to Nov-Apr — NOAA states the calving season is November through April, not into May/June."
  },

  // ===== BOWHEAD WHALE =====
  "bowheadwhale-western-arctic": {
    waypoints: [
      { lat: 71.6,lng: -156.0,  label: "Beaufort Sea summer feeding",   purpose: "feed",
        content: "Bowheads — among the longest-lived mammals on Earth, with some individuals estimated over 100 years old from harpoon-point archaeology — feed on copepods and krill along the Beaufort Sea shelf through the brief Arctic summer, using their massive heads to break through sea ice to breathe." },
      { lat: 71.8,lng: -161.5,  label: "North of Point Barrow",          purpose: "transit" },
      { lat: 70.0,lng: -168.5,  label: "Chukchi Sea transit",            purpose: "transit",
        content: "Migratory corridor through the Chukchi Sea connecting Beaufort Sea summer feeding grounds to Bering Sea wintering areas, timed around the seasonal advance and retreat of pack ice." },
      { lat: 65.0,lng: -169.3,  label: "Bering Strait, south of St. Lawrence Island", purpose: "rest",
        content: "Waters around St. Lawrence Island are used by wintering bowheads and are also a traditional Alaska Native subsistence whaling ground, where hunting continues today under a longstanding international quota managed with local whaling communities." },
      { lat: 63.5,lng: -174.0,  label: "Anadyr Gulf / SE Bering Sea",   purpose: "feed",
        content: "Southeastern Bering Sea feeding ground used by the Bering-Chukchi-Beaufort bowhead stock, the largest and healthiest of the species' surviving populations, now numbering in the thousands after being hunted to near-extinction by 19th-century commercial whaling." }
    ],
    cycleMonths: [8,9,10,11,0,1,2,3,4],
    stages: ["Summer feeding, Beaufort Sea","Westward through Chukchi Sea","Passing St. Lawrence Island","Winter range, SE Bering Sea"],
    sourceNotes: "Verified accurate. Note for future review: Szesciorka et al. 2024 (AGU/Wiley) found fall departure from the western Beaufort has shifted ~45 days later over 2008-2022 due to sea-ice loss — cycleMonths may need periodic re-verification as this trend continues."
  },

  // ===== ORCA — North Pacific split into two non-interbreeding populations =====
  "orca-southern-resident": {
    name: "Orca - Southern Resident (Salish Sea)",
    waypoints: [
      { lat: 48.4, lng: -124.7, label: "San Juan Islands / Haro Strait", purpose: "feed",
        content: "Historically the summer core habitat for the endangered Southern Resident killer whales (only around 75 remain), who feed here on Fraser River Chinook salmon. Sightings around the islands have dropped over 75% since the early 2000s as Fraser River Chinook stocks have declined, and the whales now range more widely in search of food." },
      { lat: 48.3, lng: -125.5, label: "Strait of Juan de Fuca mouth", purpose: "transit",
        content: "Open Pacific coastline used by Southern Resident killer whales ranging outside the Salish Sea in search of Chinook salmon, particularly when inland runs are poor." },
      { lat: 48.6, lng: -126.5, label: "Outer coast, SW Vancouver Island", purpose: "feed" }
    ],
    cycleMonths: [4,5,6,7,8,9],
    stages: ["Spring salmon arrival (May)","Peak chinook feeding, Salish Sea (Jun-Aug)","Increasing outer-coast use as Salish Sea time declines","Autumn migration (Sep-Oct)"],
    sourceNotes: "NOAA Fisheries; Canada DFO Recovery Strategy 2018. Split from 'orca-north-pacific-resident', which wrongly combined this genetically/culturally distinct, non-interbreeding population with Northern Residents into one route."
  },
  "orca-northern-resident": {
    name: "Orca - Northern Resident (BC coast)",
    waypoints: [
      { lat: 50.6, lng: -126.8, label: "Johnstone Strait", purpose: "feed" },
      { lat: 50.8, lng: -127.6, label: "Queen Charlotte Strait", purpose: "transit" },
      { lat: 51.2, lng: -130.0, label: "Queen Charlotte Sound, open water", purpose: "transit" },
      { lat: 53.0, lng: -134.5, label: "Northern feeding grounds", purpose: "feed" }
    ],
    cycleMonths: [6,7,8,9],
    stages: ["Spring/summer arrival","Peak chinook feeding (Jul-Aug)","Summer presence","Autumn dispersal"],
    sourceNotes: "Canada DFO Recovery Strategy 2018; Orca Conservancy. See orca-southern-resident for the split rationale."
  },

  "orca-norway-herring": {
    name: "Orca - Norway (Herring specialists)",
    waypoints: [
      { lat: 69.65,lng: 18.96,  label: "Tromsø approach",               purpose: "transit",
        content: "From roughly November to January, orcas work cooperatively with humpback whales to corral massed herring into baitballs in these fjords, driving fish to the surface — one of the most reliable orca feeding spectacles in the world." },
      { lat: 70.05,lng: 20.97,  label: "Skjervøy",                       purpose: "feed",
        content: "As Norwegian spring-spawning herring shifted their wintering grounds north over the 2016-2020 period, orca and humpback activity followed them into these fjords, which became a major feeding hotspot as the fish moved in to overwinter in dense shoals." },
      { lat: 70.2, lng: 21.6,   label: "Kvænangenfjord herring overwintering grounds", purpose: "feed" }
    ],
    cycleMonths: [10,11,0],
    stages: [
      "Herring migration begins (Nov)",
      "Peak aggregation, Skjervøy/Kvænangenfjord (Dec)",
      "Cooperative hunting displays",
      "Season ends (Jan)"
    ],
    sourceNotes: "SEVENSEAS Media; Arctic Whale Tours. Fixed invalid month index (was using '12'; valid range is 0-11). Also relocated — herring wintering grounds shifted north to Skjervøy/Kvænangenfjord in 2017/18; the old North Cape/Barents Sea point reflected an outdated pattern."
  },

  "orca-new-zealand": {
    name: "Orca - New Zealand",
    waypoints: [
      { lat: -36.6, lng: 174.85,label: "Hauraki Gulf aggregations",     purpose: "feed",
        content: "Orcas cruise the shallow harbours feeding on rays — a genuinely risky prey choice, since ray barbs occasionally kill the whales that hunt them; one young female orca was found dead here after being stung by stingray spines." },
      { lat: -36.0, lng: 175.3, label: "Offshore Northland east coast", purpose: "transit" },
      { lat: -35.2, lng: 174.3, label: "Bay of Islands sightings",      purpose: "feed",
        content: "New Zealand's small, wide-ranging orca population is the only orca group in the world known to routinely eat stingrays, eagle rays, and electric rays as a staple prey item, hunting them along shallow sandy or muddy seafloor. The Bay of Islands is one of the best-documented sites for this unique benthic ray-hunting behaviour, with sightings most likely October to March." }
    ],
    cycleMonths: [10,11,0,1,2],
    stages: [
      "Season begins (Nov)",
      "Peak sightings, summer (Dec-Feb)",
      "Sightings continue through March"
    ],
    sourceNotes: "NZ Herald; Orca Research Trust. cycleMonths extended from Oct-Dec to Nov-Mar — sources describe summer (Nov-Mar) as prime viewing, not just Oct-Dec."
  },
  "manta-reef-kona": {
    name: "Manta Ray - Reef (Hawaii, Kona Coast)",
    waypoints: [
      { lat: 19.736,lng: -156.054,label: "Garden Eel Cove / Manta Heaven, Makako Bay", purpose: "feed" },
      { lat: 19.562,lng: -155.963,label: "Keauhou Bay / Manta Village",  purpose: "feed" }
    ],
    cycleMonths: [0,1,2,3,4,5,6,7,8,9,10,11],
    stages: [
      "Year-round residents, nightly feeding",
      "76% resight rate, 100+ individuals documented"
    ],
    sourceNotes: "Manta Pacific Research Foundation. Fixed a coordinate that spliced Keauhou Bay's latitude onto Garden Eel Cove's longitude, matching neither real site (~20km apart) — now two separate real waypoints. Extended to year-round (was May-Nov only) — sources confirm nightly presence virtually every night, 365 days a year. isResident:true in the species data is correct as-is — these are genuine residents; this route reflects real nightly movement, not a seasonal migration."
  },

  "manta-reef-palau": {
    name: "Manta Ray - Reef (Palau)",
    waypoints: [
      { lat: 7.13, lng: 134.23, label: "German Channel, Palau",         purpose: "feed" }
    ],
    cycleMonths: [9,10,11,0,1,2,3,4],
    stages: [
      "Season builds (Oct)",
      "Peak mating/cleaning activity (Dec-Apr)",
      "Season winds down (May)"
    ],
    sourceNotes: "fishnfins; Solitude World; Palau Explorer; Manta ID Palau. Coordinate corrected ~30km (real German Channel sits between Ngemelis and Ngercheu islands). cycleMonths corrected from Apr-Sep to Oct-May — sources describe essentially the opposite season from what was previously listed."
  },

  "manta-reef-indonesia": {
    name: "Manta Ray - Reef (Raja Ampat, Indonesia)",
    waypoints: [
      { lat: -0.58, lng: 130.54,label: "Manta Sandy, Dampier Strait, Raja Ampat", purpose: "feed" }
    ],
    cycleMonths: [10,11,0,1,2,3,4,5],
    stages: [
      "Peak aggregation (Dec-Feb)",
      "700+ individuals documented",
      "Cleaning station density highest (Dec-Feb)"
    ],
    sourceNotes: "Bird's Head Seascape; fishnfins; Papua Paradise. Coordinate corrected ~115km to the real Manta Sandy cleaning/feeding site. Fixed invalid month index (was '12')."
  },

  "manta-giant-mozambique": {
    name: "Manta Ray - Giant (Mozambique, Tofo)",
    waypoints: [
      { lat: -23.85,lng: 35.55, label: "Tofo Beach deep channel",       purpose: "feed" }
    ],
    cycleMonths: [10,11,0,1,2,3],
    stages: [
      "Season builds (Nov)",
      "Most concentrated, Sep-Feb; mating peak Nov-Mar",
      "Season winds down (Apr)"
    ],
    sourceNotes: "mozambique.co.za; Marine Megafauna Foundation. Fixed invalid month index (was '12'). Coordinates verified accurate."
  },

  "manta-giant-ningaloo": {
    name: "Manta Ray - Giant (Australia, Ningaloo)",
    waypoints: [
      { lat: -21.83,lng: 113.97,label: "Ningaloo Reef coral spawn aggregation, North West Cape", purpose: "feed" }
    ],
    cycleMonths: [2,3,4],
    stages: [
      "Coral spawning trigger (Mar-Apr)",
      "Transient influx from open water, North West Cape",
      "Overlap with humpback whales (Apr-May)"
    ],
    sourceNotes: "Project Manta / Murdoch University. Coordinate corrected ~40-50km to the actual North West Cape/Exmouth aggregation site. NOTE: on further review of the full species record (which describes a large, year-round, 1,000+ individually catalogued population across Ningaloo broadly), isResident was left as true at the species level — only this specific waypoint documents the narrower Mar-Apr coral-spawn-driven influx at North West Cape."
  },
  "manta-reef-maldives": {
    waypoints: [
      { lat: 2.5,  lng: 72.85,  label: "Southern atoll range",          purpose: "transit" },
      { lat: 4.2,  lng: 72.85,  label: "Approach to Baa Atoll",         purpose: "transit" },
      { lat: 5.2,  lng: 73.1,   label: "Hanifaru Bay",                  purpose: "feed",
        content: "The largest known manta ray feeding station in the world. Tidal currents trap plankton against the bay's cul-de-sac shape, and on peak days around the full and new moons of the May-November season, over 100-200 mantas can gather to feed simultaneously in a chaotic, spiralling frenzy — in a bay no bigger than a soccer field." }
    ],
    cycleMonths: [4,5,6,7,8,9,10],
    stages: ["Dispersed, southern atolls","Moving toward Baa Atoll","Feeding aggregation, Hanifaru Bay (peak Aug)"],
    sourceNotes: "ZuBlu; PADI. Verified accurate, no changes."
  },

  "turtle-green-costarica": {
    name: "Sea Turtle - Green (Costa Rica, Tortuguero)",
    waypoints: [
      { lat: 10.6, lng: -83.5,  label: "Tortuguero Beach nesting",      purpose: "nest" }
    ],
    cycleMonths: [6,7,8,9],
    stages: [
      "Nesting season begins (Jul)",
      "Peak egg-laying (Aug-Sep)",
      "Season ends (Oct)"
    ],
    sourceNotes: "Tortuguero National Park (official site). cycleMonths corrected from Apr-Nov to Jul-Oct — the park's own published season is July through October, peaking Aug-Sep."
  },

  "turtle-green-australia": {
    name: "Sea Turtle - Green (Australia, Raine Island)",
    waypoints: [
      { lat: -11.6, lng: 144.0, label: "Raine Island nesting",          purpose: "nest" }
    ],
    cycleMonths: [9,10,11,0,1,2],
    stages: [
      "Nesting begins (Oct)",
      "Peak season (Nov-Jan)",
      "Season winds down (Mar)"
    ],
    sourceNotes: "Coral CoE; Great Barrier Reef Foundation. Fixed invalid month index (was '12')."
  },

  // ============================================================
  // OLIVE RIDLEY SEA TURTLES — ARRIBADA SITES
  // ============================================================
  
  "turtle-olive-mexico": {
    name: "Sea Turtle - Olive Ridley (Mexico)",
    waypoints: [
      { lat: 15.72, lng: -96.73, label: "La Escobilla, Oaxaca (world's largest arribada)", purpose: "nest" },
      { lat: 18.42, lng: -103.53,label: "Ixtapilla, Michoacán arribada beach", purpose: "nest" }
    ],
    cycleMonths: [7,8,9,10,11],
    stages: [
      "Season begins (Jul)",
      "First arribada (Aug-Sep)",
      "Peak nesting (Aug-Oct)",
      "Season ends (Nov)"
    ],
    sourceNotes: "CONANP; Mexico News Daily; Riviera Maya News. Corrected — the original single coordinate (15.7,-97.5) matched neither of these two real, distinct arribada beaches ~700km apart in different states (Oaxaca vs Michoacán); now both are represented."
  },

  "turtle-olive-costarica": {
    name: "Sea Turtle - Olive Ridley (Costa Rica, Ostional)",
    waypoints: [
      { lat: 9.95, lng: -85.85, label: "Ostional Beach arribadas",      purpose: "nest",
        content: "Ostional is one of only about eight beaches worldwide where mass nesting \"arribadas\" occur. During peak events in September-October, up to several hundred thousand females can come ashore over 3-4 nights on a roughly 28-day lunar cycle — the largest arribada on record, in November 1995, brought an estimated 500,000 turtles ashore." }
    ],
    cycleMonths: [7,8,9,10,11],
    stages: [
      "Multiple arribadas per season",
      "Peak (Aug-Sep)",
      "Synchronous nesting events (monthly)"
    ],
    sourceNotes: "Costa Rica Ministry of Environment, Ostional Refuge. Verified accurate, no changes."
  },

  "turtle-olive-india": {
    name: "Sea Turtle - Olive Ridley (India, Gahirmatha)",
    waypoints: [
      { lat: 20.6, lng: 87.3,   label: "Gahirmatha Beach largest arribada",purpose: "nest" }
    ],
    cycleMonths: [0,1,2],
    stages: [
      "Largest global arribada",
      "Peak nesting (Jan-Feb)",
      "Record: 200,000+ nests (2019)"
    ],
    sourceNotes: "Wildlife Institute of India; National Herald India. cycleMonths corrected from Feb-Apr to Jan-Mar — sources consistently cite January as the season start."
  },
  "shark-white-southafrica": {
    name: "Great White Shark - South Africa",
    waypoints: [
      { lat: -34.7, lng: 19.45, label: "Dyer Island & False Bay (historic aggregation, pre-2018)", purpose: "feed",
        content: "Once one of the densest great white populations on the planet, drawn by a large Cape fur seal colony on Dyer Island. Sightings collapsed after 2016 — cage-diving numbers dropped from 85,000 divers a year to 54,000 by 2019. Predation by two orcas, nicknamed Port and Starboard, that target sharks for their livers plays a role, but researchers attribute more of the decline to longline/gillnet bycatch, shark nets, and depletion of prey fish forcing sharks to move east." },
      { lat: -34.9, lng: 18.85, label: "South of Cape Hangklip",        purpose: "transit" },
      { lat: -34.13,lng: 18.58, label: "Seal Island (historic, largely absent since ~2018)", purpose: "feed" }
    ],
    cycleMonths: [4,5,6,7,8],
    stages: [
      "Historic season begins (Apr)",
      "Historic peak feeding season (May-Jul)",
      "Sharks have largely relocated away from False Bay/Seal Island since ~2018 (orca predation, net mortality, warming waters)",
      "Consider Mossel Bay / Gansbaai outer waters for current sightings"
    ],
    sourceNotes: "National Geographic; earthsky.org; Forbes (2025); University of Miami research. Relabeled as historic — great whites have been effectively absent from False Bay/Seal Island since roughly 2018-2019, a well-documented ecosystem shift, not a reliable current-season site."
  },
  "sardinerun-southafrica": {
    name: "Sardine Run - South Africa",
    waypoints: [
      { lat: -31.65,lng: 29.75, label: "Port St Johns aggregation",     purpose: "feed" },
      { lat: -29.9, lng: 31.3,  label: "Durban winter grounds",         purpose: "feed" }
    ],
    cycleMonths: [5,6],
    stages: [
      "Southward migration (May)",
      "Peak bait ball formations (May-Jun)",
      "Multi-species feeding event",
      "Dispersal (Jun-Jul)"
    ],
    sourceNotes: "KZN Sharks Board; shark.co.za. Verified accurate — Port St Johns (~Jun 8-9) then Durban (~Jun 25) matches documented shoal timing."
  },

  "whaleshark-donsol": {
    name: "Whale Shark - Philippines (Donsol)",
    waypoints: [
      { lat: 12.5, lng: 123.85, label: "Donsol Bay sardine/plankton aggregation", purpose: "feed",
        content: "The Donsol River carries plankton- and krill-rich nutrients into the bay from November to June (peak February-May), sustaining whale sharks — locally called butanding — for up to seven months a year. A WWF-backed community ecotourism program set the rules here early, making Donsol one of the best-managed whale shark sites in the world." },
      { lat: 12.4, lng: 124.15,label: "San Bernardino Strait, open water", purpose: "transit" }
    ],
    cycleMonths: [1,2,3,4,5],
    stages: [
      "Season begins (Feb)",
      "Peak season (Mar-May)",
      "Season ends (Jun)"
    ],
    sourceNotes: "LAMAVE (Large Marine Vertebrates Research Institute Philippines); PIA. Split from 'whaleshark-philippines', which wrongly connected this real wild seasonal aggregation to Oslob via a 14-waypoint loop with no supporting tagging study — see whaleshark-oslob."
  },
  "whaleshark-oslob": {
    name: "Whale Shark - Philippines (Oslob, provisioned)",
    waypoints: [
      { lat: 9.46, lng: 123.38, label: "Oslob provisioned feeding site (hand-fed, largely non-migratory)", purpose: "feed" }
    ],
    cycleMonths: [0,1,2,3,4,5,6,7,8,9,10,11],
    stages: [
      "Daily hand-feeding sustains near-constant presence",
      "Published research: only a small minority of tagged individuals (9 of 208) became prolonged residents in response to feeding",
      "This is a documented behavioral anomaly caused by provisioning, not natural migratory residency"
    ],
    sourceNotes: "LAMAVE (Araujo et al., PMC5627090; Meekan et al., PMC7813242). Split from 'whaleshark-philippines'. This site is ethically controversial — no tagging/tracking study supports a natural migratory connection to Donsol; shown here as its own standalone, clearly-labeled site rather than implying a real migration route."
  },

  "whaleshark-mexico": {
    name: "Whale Shark - Mexico (Yucatan Peninsula)",
    waypoints: [
      { lat: 21.7, lng: -87.35, label: "Isla Holbox seasonal",           purpose: "feed",
        content: "Warm nutrient upwellings near Holbox and neighbouring Isla Contoy create one of the world's largest whale shark feeding aggregations each summer, with sharks gathering to filter-feed on dense plankton and fish spawn." },
      { lat: 21.75,lng: -86.9,  label: "Cabo Catoche, open water",       purpose: "transit" },
      { lat: 21.25,lng: -86.55, label: "Isla Mujeres bonito aggregation",purpose: "feed",
        content: "From roughly May to September, whale sharks feed here on plankton and, notably, fish eggs — during July and August, thousands of spawning little tunny release eggs that sharks vacuum up at the surface at dawn, in an offshore aggregation zone locally called \"Afuera.\"" },
      { lat: 20.5, lng: -86.65, label: "Playa del Carmen corridor",     purpose: "transit" },
      { lat: 19.9, lng: -86.75, label: "Cozumel dispersal",             purpose: "transit" }
    ],
    cycleMonths: [4,5,6,7,8],
    stages: [
      "Aggregation begins (May)",
      "Peak feeding bonito spawning (Jun-Aug)",
      "Peak numbers (Jul-Aug)",
      "Season ends (Sep)"
    ],
    sourceNotes: "DIVE Magazine; POC Nadive Center. cycleMonths corrected from Jun-Oct to May-Sep — official regulated season is May 15-Sep 17."
  },

  "whaleshark-australia": {
    name: "Whale Shark - Australia (Ningaloo Reef)",
    waypoints: [
      { lat: -21.8, lng: 113.6, label: "Ningaloo Reef primary zone",    purpose: "feed" },
      { lat: -22.2, lng: 113.5, label: "Coral Bay feeding",             purpose: "feed" },
      { lat: -23.5, lng: 113.3, label: "Shark Bay dispersal",           purpose: "transit" }
    ],
    cycleMonths: [2,3,4,5,6,7],
    stages: [
      "Season begins (Mar)",
      "Peak plankton blooms (Apr-May)",
      "High aggregation (Apr-Jun)",
      "Season conclusion (Aug)"
    ],
    sourceNotes: "Go West Handbook; Ningaloo Whale Sharks Exmouth. cycleMonths corrected to start in March — official season is Mar 1-Jul 31."
  },

  "whaleshark-maldives": {
    name: "Whale Shark - Maldives",
    waypoints: [
      { lat: 3.5, lng: 72.85,   label: "South Ari Atoll residents",     purpose: "feed",
        content: "The South Ari Marine Protected Area (SAMPA) protects a resident population of mostly juvenile whale sharks that feed here year-round on plankton concentrated along the atoll's outer reef edge — one of very few known non-seasonal whale shark aggregation sites." },
      { lat: 4.5, lng: 72.8,    label: "Baa Atoll seasonal",            purpose: "feed" },
      { lat: 3.8, lng: 73.2,    label: "Laamu Atoll dispersal",         purpose: "transit" }
    ],
    cycleMonths: [0,1,2,3,4,5,6,7,8,9,10,11],
    stages: [
      "Year-round residents at South Ari (world's only such aggregation)",
      "Seasonal peaks (Mar-May wet season)",
      "Monsoon season variation (Jun-Sep)",
      "Dry season (Oct-Feb)"
    ],
    sourceNotes: "ZuBlu; Resortlife. Fixed invalid month indices (was 1-12, corrected to 0-11) — South Ari's year-round residency is real and well documented."
  },

  "whaleshark-galapagos": {
    name: "Whale Shark - Galápagos Islands",
    waypoints: [
      { lat: 1.68, lng: -91.98, label: "Darwin Island upwelling zone",  purpose: "feed" },
      { lat: 1.38, lng: -91.82, label: "Wolf Island deep currents",     purpose: "feed" }
    ],
    cycleMonths: [5,6,7,8,9,10],
    stages: [
      "Southbound migration (Jun-Jul)",
      "Peak upwelling season (Jul-Sep)",
      "Nutrient-rich feeding (Aug-Sep)",
      "Season ends (Nov)"
    ],
    sourceNotes: "HappyGringo. Fixed invalid month index (was '12') — principal Darwin/Wolf season is Jun-Nov, driven by pregnant female migration."
  },

  "whaleshark-mozambique": {
    name: "Whale Shark - Mozambique (Tofo)",
    waypoints: [
      { lat: -23.85,lng: 35.55, label: "Tofo Beach deep channel",       purpose: "feed" },
      { lat: -24.3, lng: 35.6,  label: "Inhambane dispersal zone",      purpose: "transit" }
    ],
    cycleMonths: [9,10,11,0,1,2],
    stages: [
      "Season begins (Oct)",
      "Peak aggregation (Nov-Dec)",
      "Southern hemisphere summer (Dec-Feb)",
      "Season ends (Mar)"
    ],
    sourceNotes: "Mozambique Travel. Fixed invalid month index (was '12') — prime Tofo season is explicitly Oct-Mar."
  },

  // ===== COMMON DOLPHIN — added from Marvi Atlas cross-check; species record
  // already existed in bc-data-part4.js and bc-globe.js's COLORS map already had
  // an entry for this key, but no MIGRATORS route existed, so it never appeared
  // on the map at all. =====
  "commondolphin-northeast-atlantic": {
    name: "Common Dolphin - Northeast Atlantic",
    waypoints: [
      { lat: 58, lng: -10,  label: "Celtic Sea / NW Scotland",   purpose: "feed",
        content: "Shelf waters northwest of Scotland where common dolphins follow seasonal concentrations of mackerel and herring, part of a wide northeastern Atlantic range that shifts with prey availability rather than a fixed migratory circuit." },
      { lat: 48, lng: -12,  label: "Bay of Biscay",              purpose: "feed",
        content: "The Bay of Biscay's continental shelf edge is one of the most important common dolphin feeding grounds in European waters, where pods numbering in the hundreds gather around dense schools of anchovy and sardine." },
      { lat: 38, lng: -15,  label: "Offshore Iberia / Azores",   purpose: "transit",
        content: "Open Atlantic waters off Iberia and the Azores, part of this species' very broad range — common dolphins are among the most widely distributed cetaceans and move opportunistically rather than along one narrow corridor." },
      { lat: 30, lng: -20,  label: "Subtropical Atlantic",       purpose: "transit",
        content: "Subtropical open-ocean range at the southern edge of this population's documented movement, reflecting the species' tolerance for a wide range of water temperatures." }
    ],
    cycleMonths: [4,5,6,7,8,9,10],
    stages: [
      "Feeding, Bay of Biscay & Celtic Sea (Jun-Oct)",
      "Ranging northwest toward Scotland",
      "Dispersing into offshore Iberia/Azores waters",
      "Widest subtropical range (autumn)"
    ],
    sourceNotes: "Marvi Atlas migrationStops.ts, cross-referencing IUCN Red List: Delphinus delphis (2017); Evans P.G.H. (2003); Moura et al. (2012); OSPAR Commission bycatch report — same sources already cited in this species' bc-data-part4.js record. cycleMonths matched to that record's months [4,5,6,7,8,9,10]."
  }
};

// End of migration routes data
window.BC_MIGRATORS_LOADED = true;
